import React, { useContext, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Adaptive, RegularButton as Button, Typography } from 'fronton-react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { styled } from 'linaria/react';
import { DataContext, RouteContext } from 'entries/common/widgets/context';
import { Pencil } from 'assets';
import { gaClickOnGoods } from 'ga-events/ga-click-goods';
import { getLocalStorageAvailable } from 'common/helpers/get-local-storage-available.helper';
import { setInputParamsInStorage } from 'common/helpers/set-input-params-in-storage.helper';
import { useMaterialState } from '../surface/store/hooks/material';
import { useSurfaces } from '../surface/hooks/use-surfaces';
import { DoorsWindowsForm } from './forms/doors-windows/doors-windows.form';
import { RoomParametersForm } from './forms/room-parameters/room-parameters.form';
import { LayerThicknessForm } from './forms/layer-thickness/layer-thickness-form';
import { Thickness } from './forms/layer-thickness/const';
import { Inputs } from './types';
import { dimensionsAtom, dimensionsFormValues, TState } from './store/dimensions';
import { getFormBody } from './helpers/get-body';
import { useFormConditions } from './hooks/use-dimension-form-conditions';
import { Snackbar } from '../common/components/Snackbar';
import { useSurfaceState } from '../surface/store/hooks/surface';
import { useFilteredInputParams } from './forms/hooks/use-filtered-input-params';
import { getFormData } from './helpers/get-form-data';
import { doorsWindowsArea, roomArea } from './store/area';
import { WarningSnackBar } from './forms/warnong-snack-bar/warning-snack-bar.form';
import { getFormattedCalculateParams } from './helpers/get-formatted-calculate-params.helper';
import { useIsFloorCalculator } from './hooks/use-is-floor-calculator.hook';
import { MeasurementBanner } from './components/MeasurementBanner/measurement-banner.component';

export const DimensionsForm = () => {
  const [hasNegativeParams, setHasNegativeParams] = useState<boolean | undefined>(false);
  const [showWarningBar, setShowWarningBar] = useState<boolean>(false);
  const [isShowSnack, setIsShowSnack] = useState<boolean>(false);

  const { setContext: setRoute } = useContext(RouteContext);
  const data = useContext(DataContext);
  const surfaces = useSurfaces();

  const isFloorCalculatorId = useIsFloorCalculator();
  const isLocalStorageAvailable = getLocalStorageAvailable();

  const { surfaceFormState } = useSurfaceState();
  const { materialFormState } = useMaterialState();

  const { filteredSurfaces } = useFilteredInputParams();

  const { hasDoorsWindows, hasThickness, hasMaterialsHeader, hasDoors } = useFormConditions();

  const saveCalculateParams = useSetRecoilState(dimensionsAtom);
  const [dimensionState, setDimensionState] = useRecoilState(dimensionsFormValues);

  const isRoomArea = useRecoilValue(roomArea);
  const isDoorsWindowsArea = useRecoilValue(doorsWindowsArea);

  const methods = useForm<Inputs>({
    defaultValues: {
      ...(hasDoors && { doors: '1' }),
      ...(hasThickness && { thickness: Thickness.horizontal }),
      ...dimensionState,
    },
  });

  const onSubmit: SubmitHandler<Inputs> = async (inputsData: Inputs) => {
    const formData = getFormData({
      data: inputsData,
      state: {
        isDoorsWindowsArea,
        isRoomArea,
      },
    });
    const body = getFormBody({
      data: formData,
      surfaces: { state: surfaceFormState, data: surfaces },
      materials: materialFormState,
      productData: data,
    });

    if (isFloorCalculatorId && isLocalStorageAvailable && data) {
      setInputParamsInStorage({
        inputParams: body.inputParameters,
        calculatorId: data.surfaces.id,
      });
    }

    try {
      const apiMethods = data!.api;
      const calculateParams: TState = await apiMethods.post(`/calculate/${data?.productId}`, body);
      const formattedCalculateParams = getFormattedCalculateParams(calculateParams);
      gaClickOnGoods({ title: 'Ввод размеров', name: 'Рассчитать количество' });
      setDimensionState(formData);
      saveCalculateParams(formattedCalculateParams);
      setRoute('calculation');
    } catch (e) {
      setIsShowSnack(true);
      setTimeout(() => {
        setIsShowSnack(false);
      }, 100);
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  return (
    <DimensionsFormContainer>
      {hasMaterialsHeader && (
        <Adaptive from="tablet">
          <TextContainer>
            <Typography variant="caption" color="text-minor">
              Выбранные поверхности
            </Typography>
            <Button
              variant="secondary"
              label="Изменить поверхности"
              size="s"
              onClick={() => {
                gaClickOnGoods({ title: 'Ввод размеров', name: 'Изменить поверхности' });
                setRoute('surface');
              }}
              iconLeft={<Pencil />}>
              Изменить поверхности
            </Button>
          </TextContainer>
          <MaterialsContainer>
            {filteredSurfaces.map(surface => (
              <MaterialInfoContainer key={surface.name}>
                <Typography variant="m" color="text-primary" size="body_accent">
                  {surface.label}
                  {materialFormState[surface.name]?.value ? ':' : null}
                </Typography>
                {materialFormState[surface.name]?.value && (
                  <Typography variant="m" color="text-secondary" size="body_long">
                    {materialFormState[surface.name].value}
                  </Typography>
                )}
              </MaterialInfoContainer>
            ))}
          </MaterialsContainer>
        </Adaptive>
      )}
      <FormProvider {...methods}>
        <FormStyled id="dimensions-form" onSubmit={methods.handleSubmit(onSubmit)}>
          <FormContainer>
            <AreaFormContainer>
              <RoomParametersForm
                hasNegativeParams={hasNegativeParams}
                setHasNegativeParams={setHasNegativeParams}
                setShowWarningBar={setShowWarningBar}
              />
            </AreaFormContainer>
            {hasDoorsWindows && (
              <AreaFormContainer>
                <DoorsWindowsForm />
              </AreaFormContainer>
            )}
            {hasThickness && (
              <AreaFormContainer>
                <LayerThicknessForm />
              </AreaFormContainer>
            )}
          </FormContainer>
          {showWarningBar && <WarningSnackBar />}
        </FormStyled>
      </FormProvider>
      <Snackbar isShow={isShowSnack} />
      <MeasurementBanner />
    </DimensionsFormContainer>
  );
};

const FormStyled = styled.form`
  width: 68%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DimensionsFormContainer = styled.div`
  display: flex;
  width: 100%;
  margin-top: 16px;

  @media (max-width: 768px) {
    display: block;
  }
`;

const TextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const MaterialsContainer = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 10px;
`;

const MaterialInfoContainer = styled.div`
  display: flex;
  gap: 4px;
`;

const AreaFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 24px;
  @media (max-width: 767px) {
    padding-top: 0;
  }
  @media (min-width: 1024px) {
    width: 80%;
  }
  @media (min-width: 768px) and (max-width: 1023px) {
    width: 80%;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  @media (max-width: 767px) {
    gap: 32px;
  }
`;
